import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Modal, Spin, message } from 'antd'
import request from '../../../../services/request'
import { useTranslation } from 'react-i18next'

export type VideoModalRef = {
    open: (fileId: string | number, title?: string) => Promise<void>
    close: () => void
}

type VideoModalProps = {
    loadingText?: string
    errorText?: string
    titleFallback?: string
    width?: number
}

const VideoModal = forwardRef<VideoModalRef, VideoModalProps>((props, ref) => {
    const { t } = useTranslation()
    const {
        loadingText = t('documents.loadingText'),
        errorText = t('documents.video.error'),
        titleFallback = t('documents.video.title'),
        width = 900,
    } = props

    const MODAL_WIDTH = width
    const MODAL_BODY_HEIGHT = Math.round((MODAL_WIDTH * 9) / 16)

    const [visible, setVisible] = useState(false)
    const [title, setTitle] = useState<string>(titleFallback as string)
    const [src, setSrc] = useState<string>('')
    const [loading, setLoading] = useState(false)

    const abortRef = useRef<AbortController | null>(null)
    const currentUrlRef = useRef<string>('')

    const revoke = () => {
        if (currentUrlRef.current) {
            URL.revokeObjectURL(currentUrlRef.current)
            currentUrlRef.current = ''
        }
    }

    const close = () => {
        revoke()
        setVisible(false)
        setSrc('')
        setTitle(titleFallback as string)
    }

    const open = async (fileId: string | number, fileTitle?: string) => {
        // отменяем предыдущий запрос, если есть
        if (abortRef.current) {
            abortRef.current.abort()
        }
        const controller = new AbortController()
        abortRef.current = controller

        setLoading(true)
        setTitle(fileTitle || (loadingText as string))
        setVisible(true)

        try {
            const resp = await request.get(`/api/documents/v1/file/${fileId}`, {
                responseType: 'blob',
                signal: controller.signal as any,
            })

            const contentType = resp?.headers?.['content-type'] || 'application/octet-stream'
            const blob = new Blob([resp?.data], { type: contentType })

            // очистка предыдущего URL (если был)
            revoke()
            const objectUrl = URL.createObjectURL(blob)
            currentUrlRef.current = objectUrl

            setSrc(objectUrl)
            setTitle(fileTitle || (titleFallback as string))
        } catch (e: any) {
            if (e?.name !== 'CanceledError' && e?.message !== 'canceled') {
                message.error(errorText)
            }
            close()
        } finally {
            setLoading(false)
        }
    }

    useImperativeHandle(ref, () => ({
        open,
        close,
    }))

    useEffect(() => {
        return () => {
            // при размонтировании: отменить запрос и освободить URL
            if (abortRef.current) {
                abortRef.current.abort()
            }
            revoke()
        }
    }, [])

    return (
        <Modal
            visible={visible}
            onCancel={close}
            footer={null}
            width={MODAL_WIDTH}
            bodyStyle={{
                width: MODAL_WIDTH,
                height: MODAL_BODY_HEIGHT,
                overflow: 'hidden',
                padding: 0,
                borderRadius: 12,
                backgroundColor: 'white',
            }}
            style={{ borderRadius: 12, overflow: 'hidden' }}
            title={title}
            centered
            destroyOnClose
            closable={false}
        >
            {loading ? (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: MODAL_BODY_HEIGHT,
                    }}
                >
                    <Spin />
                </div>
            ) : (
                <div style={{ width: '100%', height: '100%' }}>
                    <video
                        src={src}
                        controls
                        autoPlay
                        preload="metadata"
                        playsInline
                        style={{
                            width: '100%',
                            height: '100%',
                            borderRadius: 0,
                            objectFit: 'contain',
                            outline: 'none',
                        }}
                    />
                </div>
            )}
        </Modal>
    )
})

export default VideoModal
